// The incident timeline's presentation decisions, with no JSX and no value
// imports, so Node's own test runner can load it under --experimental-strip-types
// (the same constraint web/src/components/sin/sinModel.ts records).
//
// NOTHING HERE DECIDES WHETHER A DATE WAS MISSED. v_incident_timeline does that
// in Postgres, once, the same way the morning sweep does (migration 1600). This
// file only orders what came back and says it in words. Two of those sentences
// tell a named person that R50 is being raised against them, which is why
// web/test/incidentTimeline.test.ts pins them.

export type TimelineKind = 'reported' | 'update' | 'offering' | 'due' | 'status'

/** A row of v_incident_timeline, as getIncidentTimeline returns it. */
export type TimelineRow = {
  incident_id: string
  kind: TimelineKind
  kind_rank: number
  at: string
  on_date: string | null
  body: string | null
  actor_name: string | null
  person_name: string | null
  status_before: string | null
  status_after: string | null
  promised_next: string | null
  due_was: string | null
  on_time: boolean | null
  amount_zar: number | string | null
  offering_status: string | null
  settled: boolean | null
  cost_zar: number | string | null
  owner_notified: boolean | null
  location: string | null
  overdue: boolean | null
  next_offering_on: string | null
  // Migration 1601: a card reads every incident on the car, so each row says
  // which incident it belongs to. A 'status' row is the car going online or
  // offline; its status_after is 'active' or 'inactive'.
  vehicle_id?: string | null
  incident_title?: string | null
  incident_type?: string | null
  merged?: boolean | null
}

/** A row of v_vehicle_incident_cards. */
export type IncidentCard = {
  vehicle_id: string
  registration_number: string | null
  make: string | null
  model: string | null
  lifecycle_status: string | null
  commercial_model: string | null
  owner_name: string | null
  online: boolean
  offline_reasons: string[] | null
  current_location: string | null
  operational: boolean | null
  vehicle_off_road: boolean
  off_road_since: string | null
  incidents_total: number
  open_count: number
  incident_types: string[] | null
  latest_incident_at: string | null
  latest_incident_id: string | null
  latest_title: string | null
  latest_type: string | null
  latest_status: string | null
  latest_description: string | null
  next_update_date: string | null
  update_state: 'closed' | 'no_update_date' | 'update_overdue' | 'update_due_today' | 'on_track'
  in_charge_name: string | null
  nobody_in_charge: boolean
  offerings_owed_zar: number | string | null
  update_count: number
  last_update_at: string | null
  last_activity_at: string | null
  search_text: string | null
}

/**
 * Online or offline, and why, in words.
 *
 * Offline always says a reason when the view has one, because "offline" alone
 * leaves a reader to guess whether the car is at the panel beater or simply
 * parked, and those are different conversations with an owner.
 */
export function onlineLabel(card: Pick<IncidentCard, 'online' | 'offline_reasons' | 'current_location' | 'off_road_since'>): string {
  if (card.online) return 'Online'
  const why = (Array.isArray(card.offline_reasons) ? card.offline_reasons : []).filter(Boolean)
  const parts = [why.length > 0 ? why.join(', ') : card.current_location].filter(Boolean)
  const since = card.off_road_since ? `off the road since ${dayLabel(card.off_road_since)}` : null
  const tail = [...parts, since].filter(Boolean).join(', ')
  return tail ? `Offline: ${tail}` : 'Offline'
}

export type InChargeOption = { person_id: string; name: string; settlement_route: string | null }

export type IncidentUpdatePolicy = {
  today: string
  max_days: number | null
  counts_from: string | null
  rule_active: boolean | null
  payroll_release: boolean | null
  amount_zar: number | string | null
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/**
 * The calendar day a value names, as 'YYYY-MM-DD'.
 *
 * Read off the leading ten characters and never through `new Date`, because
 * postgres.js returns a `date` as midnight UTC and a browser at UTC+2 is happy to
 * show that as the day before. That is the difference between an update being on
 * time and an update costing somebody R50.
 */
export function isoDay(value: string | Date | null | undefined): string | null {
  if (value === null || value === undefined || value === '') return null
  const s = value instanceof Date ? value.toISOString() : String(value)
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null
}

/** '8 October 2026', or a dash for a date that is not known. */
export function dayLabel(value: string | Date | null | undefined): string {
  const d = isoDay(value)
  if (!d) return '—'
  const [y, m, day] = d.split('-')
  return `${Number(day)} ${MONTHS[Number(m) - 1]} ${y}`
}

/** A date-only value plus n days, in UTC so no timezone can move it. */
export function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d + n))
  return t.toISOString().slice(0, 10)
}

/**
 * The window a next update date may be chosen from: today to today plus the
 * policy's maximum. The database enforces the same window; this only stops the
 * picker offering a date that would be refused.
 */
export function nextUpdateBounds(policy: Pick<IncidentUpdatePolicy, 'today' | 'max_days'> | null): {
  min: string
  max: string
  maxDays: number
} | null {
  const today = isoDay(policy?.today)
  if (!today) return null
  const maxDays = policy?.max_days ?? 7
  return { min: today, max: addDays(today, maxDays), maxDays }
}

/**
 * Oldest first, with the next-due marker always last.
 *
 * The server already orders by time. The marker is moved to the end because it
 * is about the future: when an incident is overdue its date is in the past, and
 * sorted by time it would sit above the R50s it caused.
 */
export function orderTimeline(rows: TimelineRow[]): TimelineRow[] {
  const past = rows.filter((r) => r.kind !== 'due')
  const due = rows.filter((r) => r.kind === 'due')
  return [...past, ...due]
}

export function entryHeading(row: Pick<TimelineRow, 'kind'> & Partial<Pick<TimelineRow, 'status_after'>>): string {
  if (row.kind === 'status') return row.status_after === 'active' ? 'Back online' : 'Went offline'
  switch (row.kind) {
    case 'reported':
      return 'Reported'
    case 'update':
      return 'Update'
    case 'offering':
      return 'Sin Offering'
    case 'due':
      return 'Next update'
  }
}

// The amount as the view sent it, or null when it did not send one. It never
// falls back to R50: the rate lives in the database, and a sentence that names
// a figure nobody recorded is a missing figure shown as a real one (B16). The
// callers word the sentence without an amount instead.
function rand(value: number | string | null | undefined): string | null {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  const whole = Math.round(n * 100) % 100 === 0
  const [int, cents] = Math.abs(n).toFixed(2).split('.')
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  return `${n < 0 ? '-' : ''}R${grouped}${whole ? '' : '.' + cents}`
}

/** Whether an update kept the date it was owed by. Null when no date was owed. */
export function updateTimingSentence(row: Pick<TimelineRow, 'on_time' | 'due_was' | 'promised_next'>): string | null {
  const parts: string[] = []
  if (row.on_time === true) parts.push(`On time (due by ${dayLabel(row.due_was)}).`)
  if (row.on_time === false) parts.push(`Late: it was due by ${dayLabel(row.due_was)}.`)
  if (row.promised_next) parts.push(`Next update promised by ${dayLabel(row.promised_next)}.`)
  return parts.length > 0 ? parts.join(' ') : null
}

/** What a missed date cost, and where that charge stands. */
export function offeringSentence(
  row: Pick<TimelineRow, 'amount_zar' | 'person_name' | 'on_date' | 'offering_status' | 'settled'>,
): string {
  const who = row.person_name ?? 'the person in charge'
  const amount = rand(row.amount_zar)
  const head = `${amount ?? 'A charge, amount not recorded,'} raised against ${who} for no update by ${dayLabel(row.on_date)}.`
  if (row.settled) return `${head} Paid.`
  switch (row.offering_status) {
    case 'Approved':
      return `${head} Approved, not yet paid.`
    case 'Waived':
      return `${head} Waived, nothing is owed.`
    default:
      return `${head} Waiting on a decision, nothing is owed yet.`
  }
}

/**
 * The sentence under the timeline: when the next update is owed, by whom, and
 * what happens if it does not come.
 *
 * NOBODY IN CHARGE IS SAID OUT LOUD. It is the state of most of the backlog on
 * the day this shipped, and it means no R50 can be raised at all, which is the
 * opposite of what a reader assumes when they see a due date.
 */
export function dueSentence(
  row: Pick<TimelineRow, 'on_date' | 'person_name' | 'overdue' | 'next_offering_on' | 'amount_zar'>,
): string {
  const amount = rand(row.amount_zar)
  if (!row.person_name) {
    return `Next update due by ${dayLabel(row.on_date)}. Nobody is in charge yet, so nothing can be charged if it is missed. Choose who is in charge when you post the next update.`
  }
  if (row.overdue) {
    return `No update since it was due on ${dayLabel(row.on_date)}. ${row.person_name} is in charge. Another ${amount ?? 'charge'} is raised if there is still no update by ${dayLabel(row.next_offering_on)}.`
  }
  return `Next update due by ${dayLabel(row.on_date)}. ${row.person_name} is in charge, and ${amount ?? 'a charge'} is raised against them if no update is posted by then.`
}
